/*
 * keep in sync with backend file libs/shared/src/helpers/yup-locale.helpers.ts
 */
import { TFunction } from "i18next";
import * as yup from "yup";

import { removeMetadataPrefix } from "../helpers";
import { RydeUiUnit } from "../models";

const generateControlPath = (
  path: string,
  entity: RydeUiUnit | undefined,
  t: TFunction,
): unknown => {
  if (!path || !entity || entity === "unknown") {
    return path;
  }

  const translationKey = `${entity}.formLabel.${removeMetadataPrefix(path)}`;
  return t(translationKey);
};

export const getYupLocale = (
  entity: RydeUiUnit | undefined,
  t: TFunction,
): yup.LocaleObject => {
  const yupLocale: yup.LocaleObject = {
    mixed: {
      default: ({ path }) =>
        t("validation.invalid", {
          path: generateControlPath(path, entity, t),
        }),
      required: ({ path }) =>
        t("validation.required", {
          path: generateControlPath(path, entity, t),
        }),
      defined: ({ path }) =>
        t("validation.mustBeDefined", {
          path: generateControlPath(path, entity, t),
        }),
      notNull: ({ path }) =>
        t("validation.mustNotBeNull", {
          path: generateControlPath(path, entity, t),
        }),
      oneOf: ({ path, values }) =>
        t("validation.oneOf", {
          path: generateControlPath(path, entity, t),
          values,
        }),
      notOneOf: ({ path, values }) =>
        t("validation.notOneOf", {
          path: generateControlPath(path, entity, t),
          values,
        }),
      notType: ({ path, type, value, originalValue }) => {
        const castMsg =
          originalValue !== null && originalValue !== value
            ? ` (cast from the value \`${originalValue}\`).`
            : ".";

        return type !== "mixed"
          ? `${path} must be a \`${type}\` type, ` +
              `but the final value was: \`${originalValue}\`` +
              castMsg
          : `${path} must match the configured type. ` +
              `The validated value was: \`${originalValue}\`` +
              castMsg;
      },
    },
    string: {
      length: ({ path, length }) =>
        t("validation.exactLengthString", {
          path: generateControlPath(path, entity, t),
          length,
        }),
      min: ({ path, min }) =>
        t("validation.minLength", {
          path: generateControlPath(path, entity, t),
          min,
        }),
      max: ({ path, max }) =>
        t("validation.maxLength", {
          path: generateControlPath(path, entity, t),
          max,
        }),
      matches: ({ path, regex }) =>
        t("validation.matches", {
          path: generateControlPath(path, entity, t),
          regex,
        }),
      email: ({ path }) =>
        t("validation.email", {
          path: generateControlPath(path, entity, t),
        }),
      url: ({ path }) =>
        t("validation.url", {
          path: generateControlPath(path, entity, t),
        }),
      uuid: ({ path }) =>
        t("validation.uuid", {
          path: generateControlPath(path, entity, t),
        }),
      datetime: ({ path }) =>
        t("validation.datetime", {
          path: generateControlPath(path, entity, t),
        }),
      datetime_precision: ({ path, precision }) =>
        t("validation.datetimePrecision", {
          path: generateControlPath(path, entity, t),
          precision,
        }),

      datetime_offset: ({ path }) =>
        t("validation.datetimeOffset", {
          path: generateControlPath(path, entity, t),
        }),
      trim: ({ path }) =>
        t("validation.trim", {
          path: generateControlPath(path, entity, t),
        }),
      lowercase: ({ path }) =>
        t("validation.lowercase", {
          path: generateControlPath(path, entity, t),
        }),
      uppercase: ({ path }) =>
        t("validation.uppercase", {
          path: generateControlPath(path, entity, t),
        }),
    },
    number: {
      min: ({ path, min }) =>
        t("validation.minValue", {
          path: generateControlPath(path, entity, t),
          min,
        }),
      max: ({ path, max }) =>
        t("validation.maxValue", {
          path: generateControlPath(path, entity, t),
          max,
        }),
      lessThan: ({ path, less }) =>
        t("validation.lessThan", {
          path: generateControlPath(path, entity, t),
          less,
        }),
      moreThan: ({ path, more }) =>
        t("validation.moreThan", {
          path: generateControlPath(path, entity, t),
          more,
        }),
      positive: ({ path }) =>
        t("validation.positive", {
          path: generateControlPath(path, entity, t),
        }),
      negative: ({ path }) =>
        t("validation.negative", {
          path: generateControlPath(path, entity, t),
        }),
      integer: ({ path }) =>
        t("validation.integer", {
          path: generateControlPath(path, entity, t),
        }),
    },
    date: {
      min: ({ path, min }) =>
        t("validation.beforeDate", {
          path: generateControlPath(path, entity, t),
          min,
        }),
      max: ({ path, max }) =>
        t("validation.afterDate", {
          path: generateControlPath(path, entity, t),
          max,
        }),
    },
    boolean: {
      isValue: ({ path, value }) =>
        t("validation.isValue", {
          path: generateControlPath(path, entity, t),
          value,
        }),
    },
    object: {
      noUnknown: ({ path, unknown }) =>
        t("validation.noUnknown", {
          path: generateControlPath(path, entity, t),
          unknown,
        }),
    },
    array: {
      min: ({ path, min }) =>
        t("validation.minLengthArray", {
          path: generateControlPath(path, entity, t),
          min,
        }),
      max: ({ path, max }) =>
        t("validation.maxLengthArray", {
          path: generateControlPath(path, entity, t),
          max,
        }),
      length: ({ path, length }) =>
        t("validation.exactLengthArray", {
          path: generateControlPath(path, entity, t),
          length,
        }),
    },
    // other params should be like that:
    /*  string: {
              min: ({ path,min }) => t('validation.minLength', { path,min }),
              max: ({ max }) => t('validation.maxLength', { path,max })
           } */
  };
  return yupLocale;
};
